import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { type UserModalRef } from '../components/admin/UserModal';
import { type GroupModalRef } from '../components/admin/GroupModal';
import { useSelectedCompanyStore } from '../stores/selectedCompany.store';
import { useUserCompaniesStore } from '../stores/userCompanies.store';
import { useGetCompanyUsers, useResendInvite, useRemoveUser } from '../services/users.service';
import { useGetCompanyGroups, useDeleteGroup } from '../services/groups.service';
import { useDebouncedValue } from './useDebouncedValue';
import { isAdmin } from '../utils/permissions';
import { USER_PER_PAGE } from '../constants/userTable';

type AdminUsersTab = 'users' | 'groups';

export const useAdminUsersController = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: AdminUsersTab = searchParams.get('tab') === 'groups' ? 'groups' : 'users';
  const selectedCompanyId = useSelectedCompanyStore((state) => state.selectedCompanyId);
  const companies = useUserCompaniesStore((state) => state.companies);
  const currentUserRole = companies.find((c) => c.id === selectedCompanyId)?.role;
  const canManageGroups = !!currentUserRole && isAdmin(currentUserRole);
  const userModalRef = useRef<UserModalRef>(null);
  const groupModalRef = useRef<GroupModalRef>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const debouncedGroupSearch = useDebouncedValue(groupSearch);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<string | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, debouncedGroupSearch]);

  const { data, isLoading, error } = useGetCompanyUsers(
    selectedCompanyId || '',
    currentPage,
    USER_PER_PAGE,
    debouncedSearch,
    roleFilter,
  );
  const resendInviteMutation = useResendInvite(selectedCompanyId || '');
  const removeUserMutation = useRemoveUser(selectedCompanyId || '');
  const {
    data: groupsData,
    isLoading: isGroupsLoading,
    error: groupsError,
  } = useGetCompanyGroups(
    selectedCompanyId || '',
    currentPage,
    USER_PER_PAGE,
    debouncedGroupSearch,
  );
  const deleteGroupMutation = useDeleteGroup(selectedCompanyId || '');

  const handleUserAction = (userId: string, action: 'edit' | 'remove' | 'resend') => {
    if (action === 'resend') {
      resendInviteMutation.mutate(userId);
    } else if (action === 'edit') {
      const user = data?.items.find((u) => u.id === userId);
      if (user) {
        userModalRef.current?.openEdit(user);
      }
    } else if (action === 'remove') {
      setUserToRemove(userId);
      setIsRemoveModalOpen(true);
    }
  };

  const closeRemoveModal = () => {
    setIsRemoveModalOpen(false);
    setUserToRemove(null);
  };

  const handleRemoveUser = async () => {
    if (userToRemove) {
      await removeUserMutation.mutateAsync(userToRemove);
      closeRemoveModal();
    }
  };

  const handleRoleFilterChange = (role: string) => {
    setRoleFilter(role);
    setCurrentPage(1);
  };

  const handleTabChange = (tab: AdminUsersTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    setSearchParams(nextParams, { replace: true });
    setCurrentPage(1);
  };

  const handleGroupAction = (groupId: string, action: 'edit' | 'delete' | 'view') => {
    if (action === 'delete') {
      if (window.confirm(t('admin.groups.delete_confirm'))) {
        deleteGroupMutation.mutate(groupId);
      }
    } else if (action === 'edit') {
      const group = groupsData?.items.find((g) => g.id === groupId);
      if (group) {
        groupModalRef.current?.openEdit(group);
      }
    } else if (action === 'view') {
      navigate(`/admin/groups/${groupId}`);
    }
  };

  const handleBulkDeleteGroups = (groupIds: string[]) => {
    if (window.confirm(t('admin.groups.delete_confirm'))) {
      groupIds.forEach((groupId) => deleteGroupMutation.mutate(groupId));
    }
  };

  const handleBulkResendUsers = (userIds: string[]) => {
    userIds.forEach((userId) => resendInviteMutation.mutate(userId));
  };

  const handleBulkRemoveUsers = (userIds: string[]) => {
    if (window.confirm(t('admin.users.bulk_remove_confirm'))) {
      userIds.forEach((userId) => removeUserMutation.mutate(userId));
    }
  };

  return {
    selectedCompanyId,
    canManageGroups,
    activeTab,
    currentPage,
    search,
    setSearch,
    roleFilter,
    groupSearch,
    setGroupSearch,
    userModalRef,
    groupModalRef,
    usersData: data,
    isUsersLoading: isLoading,
    usersError: !!error,
    groupsData,
    isGroupsLoading,
    groupsError: !!groupsError,
    isRemoveModalOpen,
    isRemovePending: removeUserMutation.isPending,
    setCurrentPage,
    handleUserAction,
    handleRemoveUser,
    closeRemoveModal,
    handleRoleFilterChange,
    handleTabChange,
    handleGroupAction,
    handleBulkDeleteGroups,
    handleBulkResendUsers,
    handleBulkRemoveUsers,
  };
};
